import { Clock, MapPin, Monitor, Plus } from "lucide-react";
import { useState } from "react";
import { MockAvatarStack } from "@/src/common/components/app-mock/avatar";
import { MockPhoto } from "@/src/common/components/app-mock/media";
import { getPeople, MOCK_EVENTS_LIST, type MockCommunityEvent } from "@/src/common/components/app-mock/mock-community";
import { MockButton, MockCard, MockChip, MockSelect, MockTabs } from "@/src/common/components/app-mock/primitives";
import { CalendarWidget } from "@/src/common/components/app-mock/web-screens/calendar-widget";
import { TodaysSchedule } from "@/src/common/components/app-mock/web-screens/web-meetings-screen";

interface EventCardProps {
	event: MockCommunityEvent;
	registered: boolean;
	onToggle?: () => void;
}

function WebEventCard({ event, registered, onToggle }: EventCardProps) {
	return (
		<MockCard padded={false} dataMock="event-card">
			<div className="flex gap-3 p-3">
				<MockPhoto scene={event.scene} className="hidden h-16 w-24 shrink-0 sm:block" />
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<div className="flex items-center gap-1.5">
						<MockChip color={event.past ? "default" : "accent"} size="xs">
							{event.dateChip}
						</MockChip>
						<span className="text-[10px] text-neutral-500">{event.dateLabel}</span>
					</div>
					<p className="truncate text-[11px] font-semibold text-neutral-900">{event.title}</p>
					<p className="flex items-center gap-1 text-[10px] text-neutral-500">
						<Clock className="size-3" />
						{event.time}
					</p>
					<div className="flex flex-wrap items-center gap-1">
						<MockChip size="xs">
							{event.format === "in_person" ? <MapPin className="size-3" /> : <Monitor className="size-3" />}
							{event.format === "in_person" ? "In-person" : event.format === "hybrid" ? "Hybrid" : "Online"}
						</MockChip>
						{event.price === 0 ? (
							<MockChip color="success" size="xs">
								FREE
							</MockChip>
						) : (
							<MockChip color="accent" size="xs">
								${event.price}
							</MockChip>
						)}
						<MockAvatarStack people={getPeople(event.rsvpIds)} size={16} max={3} extra={Math.max(0, event.rsvpIds.length - 3 + 9)} />
					</div>
				</div>
				<div className="flex shrink-0 flex-col items-end gap-1">
					{event.host ? (
						<MockChip color="accent" size="xs">
							Host
						</MockChip>
					) : registered ? (
						<MockChip color="success" size="xs">
							Registered
						</MockChip>
					) : (
						<MockButton size="xs" onClick={onToggle} ariaLabel={`Register for ${event.title}`}>
							Register
						</MockButton>
					)}
					<MockButton size="xs" variant="ghost">
						View Details
					</MockButton>
				</div>
			</div>
		</MockCard>
	);
}

interface WebEventsScreenProps {
	interactive?: boolean;
	sidebar?: boolean;
	maxEvents?: number;
}

export function WebEventsScreen({ interactive, sidebar = true, maxEvents }: WebEventsScreenProps) {
	const [tab, setTab] = useState(0);
	const [registeredIds, setRegisteredIds] = useState<ReadonlyArray<string>>(
		MOCK_EVENTS_LIST.filter((event) => event.registered).map((event) => event.id),
	);
	const list = tab === 0 ? MOCK_EVENTS_LIST : MOCK_EVENTS_LIST.filter((event) => registeredIds.includes(event.id) || event.host);
	const visible = maxEvents ? list.slice(0, maxEvents) : list;
	const toggle = (id: string) =>
		setRegisteredIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

	return (
		<div className="flex gap-4" data-mock="events">
			<div className="min-w-0 flex-1 space-y-3">
				<div className="flex items-center justify-between">
					<h2 className="text-sm font-semibold text-neutral-900">Events</h2>
					<MockButton size="xs" icon={Plus}>
						Create Event
					</MockButton>
				</div>
				<div className="flex items-center justify-between gap-2">
					<MockTabs items={["All", "Registered"]} active={tab} onChange={interactive ? setTab : undefined} ariaLabel="Event filter" />
					<MockSelect value="Upcoming" />
				</div>
				<div className="space-y-2">
					{visible.map((event) => (
						<WebEventCard
							key={event.id}
							event={event}
							registered={registeredIds.includes(event.id)}
							onToggle={interactive ? () => toggle(event.id) : undefined}
						/>
					))}
				</div>
			</div>
			{sidebar ? (
				<div className="hidden w-56 shrink-0 flex-col gap-3 lg:flex" data-mock="events-sidebar">
					<CalendarWidget />
					<TodaysSchedule includeEvents />
				</div>
			) : null}
		</div>
	);
}
